import PropTypes from 'prop-types';
import React, { useState, useEffect, forwardRef, useImperativeHandle, useCallback, memo } from 'react';
import { Box, Divider, FormHelperText, IconButton, TextField } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import { DataGrid, gridClasses } from '@mui/x-data-grid';
import Iconify from '../../../components/iconify';
import { useSchemeStore } from '../schemeStore';
import DeletePopup from '../../../commons/DeletePopup';


// ________________________________________________________________________________________________________________________________________________

const StripedDataGrid = styled(DataGrid)(({ theme }) => ({
    [`& .${gridClasses.columnHeader}`]: {
        backgroundColor: 'white',
        color: 'black',
        fontWeight: 'bold',
        fontSize: '13px', // Adjust font size if needed
        lineHeight: '40px',  // Keep line height consistent
        paddingLeft: '5px', // Remove left padding

    },

    '& .MuiDataGrid-cell': {
        borderTop: `1px solid ${alpha(theme.palette.divider, 0.3)}`, // Top border for all cells
        fontSize: '13px', // Adjust font size if needed
        padding: '4px 5px', // Adjust padding to remove vertical space
        display: 'flex', // Ensure the content is aligned top
        alignItems: 'flex-start', // Align content at the top
        whiteSpace: 'normal', // Allow text to wrap
        wordWrap: 'break-word', // Ensure long words break and wrap to the next line
        overflow: 'visible',  // Make sure overflow content is visible
        height: 'auto', // Allow the cell height to expand based on content
        '&:hover': {
            backgroundColor: 'white',
        },
    },

    // Remove top border for first row
    '& .MuiDataGrid-row:first-child .MuiDataGrid-cell': {
        borderTop: 'none',  // No top border for the first row
    },

    '& .MuiDataGrid-row:last-child .MuiDataGrid-cell': {
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`,  // Bottom border for last row
    },

    '& .MuiDataGrid-row': {
        minHeight: '45px',  // Ensure no extra space vertically for rows
        display: 'flex', // Align rows as flex containers
        alignItems: 'stretch', // Stretch row content to match height
        '&:hover': {
            backgroundColor: 'white',
        },
        padding: '0px 0px'

    },

    '& .MuiDataGrid-columnSeparator': {
        display: 'none',
    },

    '& .MuiDataGrid-menuIcon': {
        display: 'none',
    },
}));

const InternalTargetGrid = memo(({ rows, onRowDelete, onDeleteAllRows, showErrorMessage, targetValues, onBlur, classificationHeaderName }) => {
    const columns = [
        {
            field: 'targetClassification',
            headerName: (typeof classificationHeaderName === 'string' && classificationHeaderName)
                ? classificationHeaderName.charAt(0).toUpperCase() + classificationHeaderName.slice(1).toLowerCase()
                : 'Target Classification',
            width: 400,
            flex: 2,
            renderCell: (params) => (

                <div style={{ whiteSpace: "normal", wordBreak: "break-word", display: 'flex', alignItems: 'center', height: '100%' }}>
                    {params.value}
                </div>
            ),
        },
        {
            field: 'actions',
            headerName: 'Target',
            width: 420,
            flex: 0.5,
            renderCell: (params) => (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <div style={{
                        transform: 'scale(0.75)',
                        transformOrigin: 'left',
                        marginRight: 8
                    }}>
                        <TextField
                            variant="outlined"
                            size="small"
                            type="number"
                            inputProps={{
                                step: "any"
                            }}
                            defaultValue={targetValues[params.row.id] !== undefined ? targetValues[params.row.id] : params.row.target || ''}
                            onBlur={(event) => onBlur(event, params.row.id)}
                        />
                    </div>

                    {
                        showErrorMessage &&
                        (targetValues[params.row.id] === '' || targetValues[params.row.id] === undefined) && (
                            <FormHelperText sx={{ color: 'red', fontSize: '10px' }}>
                                Target value required
                            </FormHelperText>
                        )
                    }
                </div>
            ),
        },

        {
            field: 'actions1',
            headerName: 'Delete',
            width: 100,
            sortable: false,
            renderHeader: () => (
                <div style={{ display: 'flex', alignItems: 'center', paddingLeft: '8px' }}>
                    <IconButton
                        onClick={onDeleteAllRows} title="Delete All"
                        sx={{ display: 'flex', alignItems: 'center', marginLeft: '0px', padding: '4px 8px 4px 0px' }}
                    >
                        <Iconify icon="fluent:delete-32-regular" width='15px' height='15px' color="red" />

                    </IconButton>
                    <span>Delete</span>
                </div>
            ), 
            renderCell: (cellValues) => (
                <IconButton onClick={(event) => onRowDelete(event, cellValues)}>
                    <Iconify icon="fluent:delete-32-regular" width="15px" height="15px" color="red" />
                </IconButton>
            ),

        },

    ];

    return (
        <Box sx={{ boxSizing: 'border-box', height: '100%', width: '100%', overflowX: 'auto' }}>
            {Array.isArray(rows) && rows?.length > 0 &&
                <StripedDataGrid localeText={{ noRowsLabel: 'Data not found' }}
                    rows={rows}
                    size='small'
                    columns={columns}
                    disableSelectionOnClick
                    getRowId={(row) => row.id}
                    getRowHeight={() => 'auto'}
                    headerHeight={35}
                    hideFooter
                    sx={{
                        height: '100%',
                        '& .odd': {
                            backgroundColor: 'background.default',
                        },
                        '& .even': {
                            backgroundColor: 'action.hover',
                        },
                    }}
                />
            }
        </Box>
    );
});

InternalTargetGrid.displayName = 'InternalTargetGrid';

InternalTargetGrid.propTypes = {
    rows: PropTypes.array.isRequired,
    onRowDelete: PropTypes.func.isRequired,
    onDeleteAllRows: PropTypes.func.isRequired,
    showErrorMessage: PropTypes.bool.isRequired,
    targetValues: PropTypes.object.isRequired,
    onBlur: PropTypes.func.isRequired,
    classificationHeaderName: PropTypes.string.isRequired,
};

const SchemeTargetDataGrid = forwardRef(({ record, handleSave, setTargetValues, targetValues, targetClassificationValue, setRecord }, ref) => { // NOSONAR


    const {

        setTargetData,

    } = useSchemeStore();


    const [rows, setRows] = useState([]);
    const [deletedRowId, setDeletedRowId] = useState([]);
    const [showErrorMessage, setShowErrorMessage] = useState(false);
    const [classificationHeaderName, setClassificationHeaderName] = useState(targetClassificationValue);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);


    const handleBlur = useCallback((event, rowId) => {
        let { value } = event.target;
        if (value < 0) {
            value = Math.abs(value); // Convert negative to positive
        } // Trim the value to remove any whitespace
        setTargetValues(prev => ({
            ...prev,
            [rowId]: value,
        }));
        const updatedIds = deletedRowId.filter(id => id !== rowId);
        setDeletedRowId(updatedIds);
    }, [deletedRowId, setTargetValues]);

    useEffect(() => {
        if (targetClassificationValue !== '') {
            setClassificationHeaderName(targetClassificationValue);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [targetClassificationValue]);

    const handleSubmit = () => {


        const targetDetails = [];


        const allValid = rows.every(row => targetValues[row.id] !== undefined && targetValues[row.id] !== '');

        if (!allValid) {

            setTargetData('error')

            setShowErrorMessage(true);
            return;
        }

        const hasEmptyValue = Object.entries(targetValues).some(([id, value]) => {

            if (!deletedRowId.includes(id) && (value === '' || value === undefined)) {
                setShowErrorMessage(true);
                return true;
            }
            return false;
        });



        // If any empty value is found, stop execution
        if (hasEmptyValue) {
            setTargetData('error')
            setShowErrorMessage(true);
            return;
        }

        // Build the targetDetails array
        Object.entries(targetValues).forEach(([id, value]) => {
            if (!deletedRowId.includes(id)) {
                targetDetails.push({
                    ClassificationId: id,
                    TargetValue: value
                });
            }
        });

        handleSave(targetDetails);
    };

    useImperativeHandle(ref, () => ({
        handleSubmit, // Exposing handleSubmit function to parent
    }));

    useEffect(() => {

        if (record && Array.isArray(record)) {
            //  console.log("targetValues in record useEffect: ", targetValues);
            const newRows = record.map((item) => {
                const [idKey, idValue] = Object.entries(item)[0];   // NOSONAR
                const [classificationKey, classificationValue] = Object.entries(item)[1];   // NOSONAR

                return {
                    id: idValue,
                    targetClassification: classificationValue,
                    target: item.Target !== undefined ? item.Target : null,
                };
            });
            setRows(newRows);

            // Initialize targetValues with existing target values
            const initialTargetValues = {};
            newRows.forEach(row => {
                if (row.target !== null) {
                    initialTargetValues[row.id] = row.target;
                }
            });
            setTargetValues(initialTargetValues);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [record]);

    useEffect(() => {
        //  console.log("deletedRowsId: ", deletedRowId);
    })
    const handleRowDelete = useCallback((event, cellValues) => {
        const rowId = cellValues.row.id;
        const updatedRows = rows.filter(row => row.id !== rowId);
        //  console.log('updatedRows: ', updatedRows);
        setRecord(updatedRows);
        setRows(updatedRows);
        setTargetValues(prev => {
            const newTargetValues = { ...prev };
            delete newTargetValues[rowId];
            return newTargetValues;
        });
        setDeletedRowId(prevDeletedRowId => [...prevDeletedRowId, rowId]);
    }, [rows, setRecord, setTargetValues]);

    const handleDeleteAllRows = useCallback(() => {
        setShowDeleteConfirmation(true);
    }, []);

    const handleConfirmDeleteAll = useCallback((confirm) => {
        if (confirm) {
            setRows([]);
            setRecord([]);
            setTargetValues({});
        }
        setShowDeleteConfirmation(false);
    }, [setRecord, setTargetValues]);

    return (
        <div style={{ display: 'flex', flexDirection: "column", height: '100%' }}>

            <style>
                {`
                          .css-191bxd1-MuiDataGrid-columnHeaderTitle {
                            font-weight: 700;
                          }
                        `}
                {`
                          .css-1vjh543-MuiDataGrid-footerContainer {
                            display: none;
                          }
                        `}
            </style>
            <div style={{ display: 'flex' }}>
                <Divider />
            </div>
            <div style={{ flexGrow: 1 }}>
                <InternalTargetGrid
                    rows={rows}
                    onRowDelete={handleRowDelete}
                    onDeleteAllRows={handleDeleteAllRows}
                    showErrorMessage={showErrorMessage}
                    targetValues={targetValues}
                    onBlur={handleBlur}
                    classificationHeaderName={classificationHeaderName}
                />
            </div>
            {showDeleteConfirmation && (
                <DeletePopup
                    setDeleteConfirm={setShowDeleteConfirmation}
                    confirm={handleConfirmDeleteAll}
                    title="Confirm Deletion"
                    msg="Are you sure you want to delete all rows?"
                />
            )}
        </div>
    );
});

export default SchemeTargetDataGrid;

SchemeTargetDataGrid.propTypes = {
    record: PropTypes.object,

    handleSave: PropTypes.func,

    setRecord: PropTypes.func,
    setTargetValues: PropTypes.func,
    targetValues: PropTypes.object,
    targetClassificationValue: PropTypes.string,
    ref: PropTypes.shape({
        current: PropTypes.any,
    }),
};


SchemeTargetDataGrid.displayName = 'SchemeTargetDataGrid';
// ________________________________________________________________________________________________________________________________________________
